/**
 * Aufgaben an Features.
 *
 * Der Store hält „Meine Aufgaben" (für die Liste und das Abzeichen in der Navigation).
 * Die Aufgaben EINES Features werden nicht global zwischengespeichert — sie gehören zur
 * Feature-Seite und werden dort direkt geladen; jede Änderung frischt aber die eigene
 * Zählung auf, damit das Abzeichen stimmt.
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { TaskDto, TaskListDto, TaskStatus } from '@pmpro/shared';
import { api } from '../api/client';

export const useTaskStore = defineStore('tasks', () => {
  const meine = ref<TaskDto[]>([]);
  const offen = ref(0);
  const laedt = ref(false);
  const fehler = ref('');
  const geladen = ref(false);

  async function ladeMeine(mitErledigten = false): Promise<void> {
    laedt.value = true;
    fehler.value = '';
    try {
      const antwort = await api.get<TaskListDto>(
        `/api/tasks/mine${mitErledigten ? '?erledigt=1' : ''}`
      );
      meine.value = antwort.items;
      offen.value = antwort.open;
      geladen.value = true;
    } catch (error) {
      fehler.value =
        error instanceof Error ? error.message : 'Die Aufgaben konnten nicht geladen werden.';
    } finally {
      laedt.value = false;
    }
  }

  /** Nur die offene Zählung auffrischen — leise, für das Abzeichen. */
  async function frischeZaehlung(): Promise<void> {
    try {
      const antwort = await api.get<TaskListDto>('/api/tasks/mine');
      offen.value = antwort.open;
    } catch {
      // Ein fehlgeschlagenes Nachzählen ist kein Grund, die Ansicht zu stören.
    }
  }

  async function ladeFuerFeature(featureId: string): Promise<TaskDto[]> {
    const antwort = await api.get<TaskListDto>(`/api/features/${featureId}/tasks`);
    return antwort.items;
  }

  async function zuweisen(featureId: string, daten: Record<string, unknown>): Promise<TaskDto> {
    const task = await api.post<TaskDto>(`/api/features/${featureId}/tasks`, daten);
    void frischeZaehlung();
    return task;
  }

  async function setzeStatus(id: string, status: TaskStatus): Promise<TaskDto> {
    const task = await api.patch<TaskDto>(`/api/tasks/${id}`, { status });
    void frischeZaehlung();
    return task;
  }

  async function zuruecknehmen(id: string): Promise<void> {
    await api.delete(`/api/tasks/${id}`);
    void frischeZaehlung();
  }

  return {
    meine,
    offen,
    laedt,
    fehler,
    geladen,
    ladeMeine,
    frischeZaehlung,
    ladeFuerFeature,
    zuweisen,
    setzeStatus,
    zuruecknehmen
  };
});
